<template>

<app-content :loading="is.loading">

	<app-content-head back="Convention.Schedule.Event" title="Manage event" />

	<app-content-body v-if="!is.loading">

		<app-content-alert theme="blue" icon="info" v-if="isApproved" :is-inline="true">

			Your event was accepted after review. This means you can only edit cosmetic details such as the description, image, minimum experience, etc.

		</app-content-alert>

		<app-content-box>

			<app-input-suggest label="Game" v-on:textchange="onGameNameChange" placeholder="Search game by name" image="image" v-model="model.game" api="bgg/game" :validation="$v.model.game" />
			<app-input-text label="Name" placeholder="Enter name..." v-model="model.name" :validation="$v.model.name" :maxlength="128" />
			<app-input-text label="Description" :autogrow="true" placeholder="Enter description..." v-model="model.description" :validation="$v.model.description" />
			<app-input-image label="Image" v-model="model.image" :validation="$v.model.image" notes="Image used must be relevant to the event and be appropriate for all ages." />
			<app-input-toggle :full-width="true" label="Min. Age" :slim="true" v-model="model.age" :validation="$v.model.age" :options="ageOptions" />
			<app-input-checklist :collapsed="true" fullWidth="true" :singular="true" :columns="1" v-if="hasGame" label="Min. Experience" v-model="model.experience" :validation="$v.model.experience" :options="$constants.schedule.event.experienceName" :descriptions="$constants.schedule.event.experienceDescription" />
			<app-input-checklist :collapsed="true" fullWidth="true" :singular="true" :columns="1" v-if="hasGame" label="Teacher Wanted" v-model="model.teacher" :validation="$v.model.teacher" :options="$constants.schedule.event.teacherName" :descriptions="$constants.schedule.event.teacherDescription" />
			<app-input-select label="Type" placeholder="Select type" v-model="model.type" :validation="$v.model.type" :options="typeOptions" v-if="references.types.length" />
			<app-input-checklist v-if="references.lobbies.length" :max="lobbyLimit" label="Lobbies" notes="Select lobbies to help make your events easier to find." v-model="model.lobbies" :validation="$v.model.lobbies" :options="lobbyOptions" />

		</app-content-box>

		<app-content-box v-if="!isApproved" title="Seats & Schedule">

			<app-input-toggle :label="(model.ticket) ? 'Capacity' : 'Total seats'" :full-width="true" :slim="true" v-model="model.seats.total" :validation="$v.model.seats.total" :options="seatOptions" :notes="(model.ticket) ? 'The total number of seats available.' : 'The total number of players.'" />
			
			<app-input-toggle label="Are you playing?" :full-width="true" v-model="model.seats.host" :validation="$v.model.seats.host" :options="hostOptions" />
        
			<app-input-text label="Enter number" v-model="model.seats.totalOther" :validation="$v.model.seats.totalOther" notes="Must be a number between 1 and 1000." v-if="model.seats.total === 1001" />
			
			<app-input-toggle label="Blocked seats" :full-width="true" :slim="true" v-model="model.seats.blocked" :validation="$v.model.seats.blocked" :options="blockOptions" notes="The number of seats blocked for other people not on this system." v-if="model.seats.total <= 8 && hasGame" />
			
			<app-input-text label="Blocked seats" :full-width="true" v-model="model.seats.blockedOther" :validation="$v.model.seats.blockedOther" notes="Must be a number between 0 and 1000." v-if="model.seats.total > 8 && model.seats.total !== 1002 && hasGame" />

			<app-input-scheduletime 
				v-on:picker="is.picking = $event" 
				:has-picker="room.spaces" 
				:room="room" 
				label="Time" 
				v-model="model" 
				:is-custom="true" 
				v-on:space="model.space = $event" 
				v-on:spaces="onSpacesChange" 
				v-on:day="model.day = $event" 
				v-on:time="model.time = $event" 
				v-on:duration="model.duration = $event" 
			/>
			
			<app-input-select v-if="!room.spaces && $_.keys(references.rooms).length > 1" label="Room" :options="references.rooms" v-model="model.location.room" notes="You can switch to another open room." />
			
			<app-input-text v-if="!room.spaces" label="Location" maxlength="255" v-model="model.location.directions" placeholder="Enter directions" notes="You can describe the location of your event within it's room, if still unknown leave blank." />

		</app-content-box>

		<app-content-box title="Moderator Settings" v-if="$isModerator && !isApproved">

			<app-input-toggle label="Official" v-model="model.official" notes="Is the event labelled as an official event." :validation="$v.model.official" />
			
			<app-input-toggle label="Enable Clashing" v-model="model.clashes.enabled" :validation="$v.model.clashes.enabled" notes="Turn off clashing if this is an event that attendees can participate in at any time." />
			
			<app-input-select label="Ticketed" v-model="model.ticket" notes="Is a ticket needed to take a seat on this event." :allow-deselect="true" placeholder="Not ticketed" :validation="$v.model.ticket" v-if="$util.count(references.tickets)" :options="references.tickets" />
			
			<app-input-text v-if="$isModerator" label="Custom Host" maxlength="32" :toggle="model.host.enabled" v-on:toggle="model.host.enabled = !model.host.enabled" notes="Customise the name of the host to something other than the user that created the event." placeholder="Enter name..." v-model="model.host.name" :validation="$v.model.host.name" />
		
		</app-content-box>

		<app-button text="Save" :disabled="!isValid" v-on:click="onSaveClick" :loading="is.saving" />

	</app-content-body>

	<app-spaces v-if="is.picking" v-on:close="is.picking = false" :is-picking="is.picking" :room="room" :spaces="references.spaces" :scheduled="references.scheduled" :allow-multiple="$isModerator" />

</app-content>

</template>

<script>

import mixForm from '@/mixin/form'
import { required } from 'vuelidate/lib/validators'

export default {

	mixins: [mixForm],

	data: function() {

		return {
			standalone: true,
			is: {
				picking: false
			},
			references: {
				room: {},
				rooms: [],
				tickets: [],
				types: [],
				days: [],
				times: [],
				spaces: [],
				lobbies: [],
				clashing: [],
                scheduled: []
			},
			hostOptions: {
				0: 'Yes, I am one of the players',
				1: 'No, I\'m hosting/teaching/organizing'
			},
			locationTypeOptions: {
				0: 'Official table',
				1: 'Other space'
			},
			seatOptions: {
				1: 1,
				2: 2,
				3: 3,
				4: 4,
				5: 5,
				6: 6,
				7: 7,
				8: 8,
				1001: 'Other',
				1002: 'Unlimited'
			},
			seatOptionsReduced: {
				1: 1,
				2: 2,
				3: 3,
				4: 4,
				5: 5,
				6: 6,
				7: 7,
				8: 8
			},
			model: {
				official: 0,
				type: 0,
				ticket: '',
				name: '',
				image: '',
				teacher: 0,
				age: 0,
				spaces: [],
				space: '',
				room: '',
				game: 0,
				experience: 0,
				description: '',
				lobbies: [],
				host: {
					enabled: 0,
					name: ''
				},
				seats: {
					host: 0,
					total: 4,
					totalOther: 0,
					blocked: 0,
					blockedOther: 0
				},
				clashes: {
					enabled: 1
				},
				day: 0,
				time: 0,
				duration: 0,
				location: {
					skip: 0,
					room: 0,
					space: 0,
					custom: 0,
					directions: ''
				}
			}
		}

	},

	validations: {
		model: {
			clashes: {},
			host: {},
			name: {
				required
			},
			day: {
				required
			},
			time: {
				required
			},
			duration: {
				required
			},
			seats: {
				total: {
					required
				}
			},
			location: {
				skip: {},
				room: {},
				space: {},
				custom: {},
				directions: {}
			}
		}
	},

	computed: {

		isApproved: function() {
		
			return this.item.approved 

		},

		room: function() {
		
			return this.references.room

		},

        isValidTime: function() {
        
            if (this.isApproval) {

                var count = 0

                this.$_.each(this.model.preferred, function(preferred) {
                
                    if(preferred.day && preferred.duration && preferred.time && (preferred.space || !this.hasSpaces)) {
                    
                        count++
                    
                    }

                }.bind(this))

                return count === 3
            
            } else {

                return this.model.day && this.model.duration && this.model.time && (this.model.space || !this.hasSpaces)
            
            }

        },

        hasSpaces: function() {
        
            return this.room.spaces

        },

		lobbyLimit: function() {

			return (this.$settings.schedule.events.submit.lobbies.max.enabled) ? this.$settings.schedule.events.submit.lobbies.max.limit : false

		},

		selectedType: function() {

			return (this.model.type) ? this.$_.findWhere(this.references.types, {
				id: this.model.type
			}) : false

		},

		hasGame: function() {

			return (this.selectedType) ? this.selectedType.games.enabled : true

		},

		blockOptions: function() {

			var seats = {}
			var total = (this.model.seats.total > 8) ? this.model.seats.totalOther || 8 : this.model.seats.total || 8

			for (var i = 0; i <= total; i++) {

				seats[i] = i

			}

			return seats
		},

		typeOptions: function() {

			var options = {}

			this.$_.each(this.references.types, function(type) {

				options[type.id] = type.name

			})

			return options

		},

		lobbyOptions: function() {

			var options = {}

			this.$_.each(this.references.lobbies, function(type) {

				options[type.id] = type.name

			})

			return options

		},

		ageOptions: function() {

			return this.$constants.schedule.ageShort

		},

		gameChosen: function() {

			return this.model.game

		}

	},

	watch: {

		gameChosen: function() {

			this.model.image = (this.model.game) ? 'https://img.geekgroup.app/api/games/avatar/' + this.model.game : ''

		}

	},

	methods: {

		onSpacesChange: function(value) {
		
			this.model.spaces = value

		},

		onModelUpdate: function() {

			var blockTotal = this.model.seats.blocked
			var seatsTotal = this.model.seats.total
			
			var blockTotalOther = 0

			if (seatsTotal > 8) {
				
				blockTotalOther = blockTotal

			}
			
			var seatsTotalOther = 0

			if (seatsTotal > 8) {
				
				seatsTotalOther = seatsTotal
				seatsTotal = 1001

			}
				
			if (seatsTotal === 0) {
				
				seatsTotal = 1002

			}

			this.model.seats.total = seatsTotal
			this.model.seats.totalOther = seatsTotalOther
			this.model.seats.blocked = blockTotal
			this.model.seats.blockedOther = blockTotalOther

		},

		onSaved: function() {

			this.$router.push({
				name: 'Convention.Schedule.Event',
				params: {
					id: this.model.id
				}
			})

		},

		onGameNameChange: function(name) {

			if (this.is.initialised) {

				this.model.name = name

			}

		}

	}

}

</script>

<style scoped>

.location-empty {
	font-size: 14px;
	line-height: 20px;
	margin-top: 10px;
	color: #707070;
	text-align: center;
	border: 1px solid #eee;
	padding: 20px;
	border-radius: 4px;
}

.location-none {
	font-size: 14px;
	line-height: 20px;
	margin-top: 10px;
	color: #fff;
	text-align: center;
	border: 1px solid #eee;
	padding: 20px;
	background-color: #f65d5d;
	border-radius: 4px;
}

.location-notice {
	font-size: 14px;
	line-height: 20px;
	margin-top: 10px;
	color: #fff;
	text-align: center;
	border: 1px solid #eee;
	padding: 20px;
	background-color: #4082d3;
	border-radius: 4px;
}

.is-device .location-empty,
.is-device .location-none,
.is-device .location-notice {
	margin-top: 20px;
	padding: 20px;
}

</style>